import { create } from 'zustand'
import { devtools } from 'zustand/middleware'
import type { WalletState, Reward, ClaimVoucher } from '../types'

interface WalletStore {
  // Wallet State
  isConnected: boolean
  address: string | null
  chainId: number | null
  balance: string | null
  isConnecting: boolean
  error: string | null

  // Rewards
  pendingRewards: Reward | null
  claimHistory: ClaimVoucher[]

  // Actions
  connectWallet: () => Promise<boolean>
  disconnectWallet: () => void
  switchNetwork: (chainId: number) => Promise<boolean>
  addNetwork: (chainId: number) => Promise<boolean>
  updateBalance: () => Promise<void>

  // Reward Actions
  setPendingRewards: (rewards: Reward) => void
  addClaimToHistory: (voucher: ClaimVoucher) => void
  clearPendingRewards: () => void

  // Error handling
  setError: (error: string | null) => void
  clearError: () => void
}

// Network configurations
export const NETWORKS = {
  base: {
    chainId: 8453,
    chainName: 'Base',
    rpcUrls: ['https://mainnet.base.org'],
    blockExplorerUrls: ['https://basescan.org'],
    nativeCurrency: {
      name: 'Ether',
      symbol: 'ETH',
      decimals: 18
    }
  },
  baseSepolia: {
    chainId: 84532,
    chainName: 'Base Sepolia',
    rpcUrls: ['https://sepolia.base.org'],
    blockExplorerUrls: ['https://sepolia.basescan.org'],
    nativeCurrency: {
      name: 'Ether',
      symbol: 'ETH',
      decimals: 18
    }
  },
  bsc: {
    chainId: 56,
    chainName: 'BNB Smart Chain',
    rpcUrls: ['https://bsc-dataseed.binance.org'],
    blockExplorerUrls: ['https://bscscan.com'],
    nativeCurrency: {
      name: 'BNB',
      symbol: 'BNB',
      decimals: 18
    }
  },
  bscTestnet: {
    chainId: 97,
    chainName: 'BNB Smart Chain Testnet',
    rpcUrls: ['https://data-seed-prebsc-1-s1.binance.org:8545'],
    blockExplorerUrls: ['https://testnet.bscscan.com'],
    nativeCurrency: {
      name: 'BNB',
      symbol: 'BNB',
      decimals: 18
    }
  }
}

export const SUPPORTED_NETWORKS = [8453, 84532, 56, 97] // Base, Base Sepolia, BSC, BSC Testnet

export const useWalletStore = create<WalletStore>()(
  devtools(
    (set, get) => ({
      // Initial State
      isConnected: false,
      address: null,
      chainId: null,
      balance: null,
      isConnecting: false,
      error: null,
      pendingRewards: null,
      claimHistory: [],

      // Wallet Actions
      connectWallet: async (): Promise<boolean> => {
        if (typeof window === 'undefined' || !window.ethereum) {
          set({ error: 'MetaMask is not installed' })
          return false
        }

        set({ isConnecting: true, error: null })

        try {
          const accounts = await window.ethereum.request({
            method: 'eth_requestAccounts'
          })

          if (accounts.length === 0) {
            set({ error: 'No accounts found', isConnecting: false })
            return false
          }

          const address = accounts[0]
          const chainId = await window.ethereum.request({
            method: 'eth_chainId'
          })

          // Convert chainId from hex to number
          const chainIdNumber = parseInt(chainId, 16)

          // Check if network is supported
          if (!SUPPORTED_NETWORKS.includes(chainIdNumber)) {
            set({
              error: `Unsupported network. Please switch to Base, BNB Chain, or their testnets`,
              isConnecting: false
            })
            return false
          }

          // Get balance
          const balance = await window.ethereum.request({
            method: 'eth_getBalance',
            params: [address, 'latest']
          })

          set({
            isConnected: true,
            address,
            chainId: chainIdNumber,
            balance: balance,
            isConnecting: false,
            error: null
          })

          return true
        } catch (error: any) {
          console.error('Wallet connection error:', error)
          set({
            error: error.message || 'Failed to connect wallet',
            isConnecting: false
          })
          return false
        }
      },

      disconnectWallet: () => {
        set({
          isConnected: false,
          address: null,
          chainId: null,
          balance: null,
          error: null,
          pendingRewards: null
        })
      },

      switchNetwork: async (chainId: number): Promise<boolean> => {
        if (typeof window === 'undefined' || !window.ethereum) {
          set({ error: 'MetaMask is not installed' })
          return false
        }

        try {
          const chainIdHex = `0x${chainId.toString(16)}`

          await window.ethereum.request({
            method: 'wallet_switchEthereumChain',
            params: [{ chainId: chainIdHex }]
          })

          set({ chainId, error: null })
          return true
        } catch (error: any) {
          // If the network is not added, try to add it
          if (error.code === 4902) {
            return get().addNetwork(chainId)
          }

          set({ error: error.message || 'Failed to switch network' })
          return false
        }
      },

      addNetwork: async (chainId: number): Promise<boolean> => {
        if (typeof window === 'undefined' || !window.ethereum) {
          set({ error: 'MetaMask is not installed' })
          return false
        }

        const network = Object.values(NETWORKS).find(n => n.chainId === chainId)
        if (!network) {
          set({ error: 'Unknown network' })
          return false
        }

        try {
          await window.ethereum.request({
            method: 'wallet_addEthereumChain',
            params: [network]
          })

          set({ chainId, error: null })
          return true
        } catch (error: any) {
          set({ error: error.message || 'Failed to add network' })
          return false
        }
      },

      updateBalance: async () => {
        const { address, isConnected } = get()

        if (!isConnected || !address || typeof window === 'undefined' || !window.ethereum) {
          return
        }

        try {
          const balance = await window.ethereum.request({
            method: 'eth_getBalance',
            params: [address, 'latest']
          })

          set({ balance })
        } catch (error: any) {
          console.error('Failed to update balance:', error)
          set({ error: error.message || 'Failed to update balance' })
        }
      },

      // Reward Actions
      setPendingRewards: (rewards: Reward) => {
        set({ pendingRewards: rewards })
      },

      addClaimToHistory: (voucher: ClaimVoucher) => {
        set((state) => ({
          claimHistory: [...state.claimHistory, voucher]
        }))
      },

      clearPendingRewards: () => {
        set({ pendingRewards: null })
      },

      // Error handling
      setError: (error: string | null) => {
        set({ error })
      },

      clearError: () => {
        set({ error: null })
      }
    }),
    {
      name: 'shapes-td-wallet-store'
    }
  )
)

// Extend the Window interface for MetaMask
declare global {
  interface Window {
    ethereum?: {
      request: (args: { method: string; params?: any[] }) => Promise<any>
      on: (event: string, handler: (...args: any[]) => void) => void
      removeListener: (event: string, handler: (...args: any[]) => void) => void
    }
  }
}